<template>
    <v-app class="ppv2-bg-white dark:ppv2-bg-dark">
        <v-container fluid class="ppv2-h-full">
            <!-- Full-page loading skeleton while fetching experience data -->
            <div v-if="isPageLoading">
                <CustomButtonSave v-if="!complited" style="pointer-events: none; opacity: 0.3" />
                <v-row class="ppv2-flex ppv2-h-full">
                    <!-- Skeleton sidebar — mirrors real sidebar exactly -->
                    <v-col
                        class="col col-md-2 col-sm-3 col-4 stepper-container"
                        :class="[{ 'stepper-container-edit !ppv2-pt-[40px]': complited }, { 'mt-40': !complited }]"
                    >
                        <div
                            v-for="i in 6"
                            :key="i"
                            :class="['step-item', { '!ppv2-min-h-[44px] !ppv2-items-center': complited }]"
                            :style="{ minHeight: complited ? undefined : '80px' }"
                        >
                            <!-- Circle (only in create mode) -->
                            <div v-if="!complited" class="circle" style="opacity: 0.2"></div>

                            <!-- Line between steps (only in create mode) -->
                            <div v-if="i < 6 && !complited" class="line" style="opacity: 0.15"></div>

                            <!-- Step text -->
                            <div
                                class="d-flex flex-column justify-content-center"
                                :style="{ marginLeft: normalizeSize(22, true) }"
                            >
                                <div
                                    v-if="!complited"
                                    class="skeleton-line"
                                    style="width: 50px; height: 8px; margin-bottom: 6px"
                                ></div>
                                <div
                                    class="skeleton-line"
                                    :style="{ width: 60 + i * 10 + 'px', height: complited ? '13px' : '12px' }"
                                ></div>
                            </div>
                        </div>
                    </v-col>

                    <!-- Skeleton content — mirrors real step content col-10 -->
                    <v-col class="col col-md-10 col-sm-9 col-8 step-content px-0 !ppv2-pt-[46px]">
                        <div
                            class="ppv2-flex ppv2-flex-wrap ppv2-w-full md:ppv2-gap-[3.125vw]"
                            :style="{ paddingLeft: normalizeSize(55) }"
                        >
                            <!-- Skeleton form section — inline styles match Step2's scoped .form-section -->
                            <div style="flex: 1; max-width: 60%">
                                <!-- HeaderStep: "ÉTAPE 1" label -->
                                <div style="margin-bottom: 40px">
                                    <div class="skeleton-line" style="width: 80px; height: 13px; margin-bottom: 8px"></div>
                                    <!-- Title: "Créer une expérience" -->
                                    <div class="skeleton-block" style="width: 280px; height: 36px"></div>
                                </div>

                                <!-- Input: "Nom de votre expérience*" -->
                                <div class="skeleton-line" style="width: 185px; height: 13px; margin-bottom: 10px"></div>
                                <div
                                    class="skeleton-block"
                                    style="height: 48px; width: 100%; border-radius: 12px; margin-bottom: 20px"
                                ></div>

                                <!-- Textarea: "Description de votre expérience" -->
                                <div class="skeleton-line" style="width: 230px; height: 13px; margin-bottom: 10px"></div>
                                <div
                                    class="skeleton-block"
                                    style="height: 120px; width: 100%; border-radius: 12px; margin-bottom: 20px"
                                ></div>

                                <!-- Upload zone: "Illustration de votre expérience" -->
                                <div class="skeleton-line" style="width: 240px; height: 13px; margin-bottom: 10px"></div>
                                <div class="skeleton-upload-box"></div>

                                <!-- Buttons: Retour + Suivant -->
                                <div
                                    class="ppv2-flex ppv2-items-center"
                                    :class="complited ? 'ppv2-justify-end' : 'ppv2-justify-between'"
                                >
                                    <div v-if="!complited" class="skeleton-line" style="width: 65px; height: 14px"></div>
                                    <div class="skeleton-block" style="width: 130px; height: 46px; border-radius: 25px"></div>
                                </div>
                            </div>

                            <!-- Skeleton preview — inline styles match PreviewExperience's scoped .preview-section -->
                            <div style="flex: 0.6; background: #d9d9d933; display: flex; flex-direction: column; padding: 40px">
                                <!-- "👁 Prévisualisation en direct" -->
                                <div class="ppv2-flex ppv2-items-center" style="gap: 10px; margin-bottom: 18px; opacity: 0.4">
                                    <div class="skeleton-circle-sm"></div>
                                    <div class="skeleton-line" style="width: 150px; height: 10px"></div>
                                </div>
                                <!-- Progress bar -->
                                <div class="skeleton-progress">
                                    <div class="skeleton-progress-bar"></div>
                                </div>
                                <!-- "Temps total estimé" -->
                                <div class="skeleton-line" style="width: 55%; height: 10px; margin-bottom: 20px"></div>
                                <!-- Title preview -->
                                <div class="skeleton-line" style="width: 40%; height: 16px; margin-bottom: 10px"></div>
                                <!-- Description preview -->
                                <div class="skeleton-line" style="width: 70%; height: 12px; margin-bottom: 20px"></div>
                                <!-- Image preview -->
                                <div
                                    class="skeleton-block"
                                    style="width: 100%; height: 180px; border-radius: 12px; margin-bottom: 20px"
                                ></div>
                                <!-- Detail lines -->
                                <div class="skeleton-line" style="width: 85%; height: 10px; margin-bottom: 8px"></div>
                                <div class="skeleton-line" style="width: 90%; height: 10px; margin-bottom: 8px"></div>
                                <div class="skeleton-line" style="width: 70%; height: 10px; margin-bottom: 8px"></div>
                                <div class="skeleton-line" style="width: 50%; height: 10px"></div>
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </div>

            <template v-else>
                <CustomButtonSave @click="handleSaveAndExit" v-if="![1, 8].includes(step) && !complited" />
                <div
                    v-if="complited && step !== 8"
                    :style="{
                        position: 'absolute',
                        top: '25px',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        gap: '8px',
                        right: normalizeSize(50, true),
                        zIndex: 10,
                    }"
                    @click="goToExperiencesList"
                >
                    <ChevronLeft color="#505d92" :size="20" />
                    <span class="ppv2-text-black ppv2-text-opacity-70" :style="{ fontSize: '16px' }">
                        {{ $t('experience.backToExperiences') }}
                    </span>
                </div>
                <v-row class="ppv2-flex ppv2-h-full">
                    <!-- Step headers on the left -->
                    <v-col
                        v-if="step !== 8"
                        class="stepper-container"
                        :class="[
                            'col col-md-2 col-sm-3 col-4',
                            { 'stepper-container-edit !ppv2-pt-[40px]': complited },
                            { 'mt-40': !complited },
                        ]"
                    >
                        <div
                            v-for="(s, index) in steps.filter((step) => step.id !== 8)"
                            :key="s.id"
                            :class="[
                                'step-item',
                                {
                                    disabled: !complited && isStepDisabled(s.id),
                                    completed: isStepCompleted(s.id) && !complited,
                                    completedMenu: isStepCompleted(s.id) && complited,
                                    active: step === s.id && !complited,
                                    activeMenu: step === s.id && complited,
                                },
                                { '!ppv2-min-h-[44px] !ppv2-items-center': complited },
                            ]"
                            @click="handleStepClick(s.id)"
                        >
                            <!-- Cercle -->
                            <div class="circle" v-if="!complited">
                                <Check
                                    v-if="isStepCompleted(s.id)"
                                    :color="theme.mode === 'dark' ? '#060B28' : 'white'"
                                    :size="18"
                                />
                            </div>

                            <!-- Ligne entre étapes -->
                            <div
                                v-if="s.id !== 7 && index < steps.length - 1 && !complited"
                                class="line"
                                :class="{ completed: isStepCompleted(s.id) }"
                            ></div>

                            <!-- Texte -->
                            <div
                                class="d-flex flex-column justify-content-center"
                                :style="{ marginLeft: normalizeSize(22, true) }"
                            >
                                <div v-if="!complited" class="step-title ppv2-text-black" :style="{ fontSize: '10px' }">
                                    {{ $t('experience.step') }} {{ s.stepNumber }}
                                </div>
                                <div
                                    class="step-subtitle ppv2-text-black"
                                    :class="{ '!ppv2-opacity-100': complited }"
                                    :style="{ fontSize: complited ? sizes.subtitleMenu : sizes.subtitle }"
                                >
                                    {{ s.subtitle }}
                                </div>
                            </div>
                        </div>
                    </v-col>

                    <!-- Step content on the right -->
                    <v-col
                        :class="{
                            'col step-content px-0 col-md-10 col-sm-9 col-8': step !== 8,
                            'col col-md-12 col-sm-12 col-12': step === 8,
                            '!ppv2-pt-[46px]': step !== 55 && step !== 8,
                            '!ppv2-py-0': step === 55,
                        }"
                    >
                        <!-- Mini loading spinner when switching steps in edit mode -->
                        <div
                            v-if="isStepTransitioning"
                            class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-py-20"
                            style="min-height: 300px"
                        >
                            <div
                                class="ppv2-animate-spin ppv2-rounded-full ppv2-h-8 ppv2-w-8 ppv2-border-4 ppv2-border-purple-600 ppv2-border-t-transparent"
                            ></div>
                        </div>
                        <v-stepper v-else class="ppv2-bg-white dark:ppv2-bg-dark" v-model="step">
                            <v-stepper-content
                                v-for="s in steps"
                                :key="s.id"
                                :step="s.id"
                                class="mr-0 px-0 ppv2-h-full"
                                :class="{ '!ppv2-py-0': s.id === 55 }"
                            >
                                <transition name="fade-step" mode="out-in">
                                    <div class="mb-4 ppv2-bg-white dark:ppv2-bg-dark" :key="s.id + '-' + step">
                                        <component
                                            :is="s.component"
                                            :ref="'step' + s.id"
                                            :step="s"
                                            :active-step="step"
                                            :sizes="sizes"
                                            :id="id"
                                            :prestation="localPrestation"
                                            :spaces_id="localSpacesId"
                                            :selected_experience="localSelectType"
                                            :country_vat_rates="country_vat_rates"
                                            :category="category"
                                            :sub_category="sub_category"
                                            :products="localProducts"
                                            :cats="cats"
                                            :token="token"
                                            @update-global-products="handleUpdateGlobalProducts"
                                            @next-step="handleNextStep(s.id, $event)"
                                            @previous-step="goToPreviousStep(s.id)"
                                            @save-and-exit="handleSaveAndExit"
                                            @update-prestation="localPrestation = $event"
                                            :complited="complited"
                                            :hide-preview="shouldHidePreview"
                                            :url_front_reservation="urlFrontReservation"
                                            :tenant="tenant"
                                            :plan-formule="planFormule"
                                            :plan-forfait="planForfait"
                                            :plan-abonnement="planAbonnement"
                                            :plan-publish-additional-sales="planPublishAdditionalSales"
                                            :permission-create-additional-sales="permissionCreateAdditionalSales"
                                            :permission-modify-additional-sales="permissionModifyAdditionalSales"
                                            :permission-delete-additional-sales="permissionDeleteAdditionalSales"
                                        >
                                        </component>
                                    </div>
                                </transition>
                            </v-stepper-content>
                        </v-stepper>
                    </v-col>
                </v-row>
            </template>

            <!-- Confirm navigation dialog -->
            <CustomDialog
                v-model="showConfirmDialog"
                :defaultTitle="$t('experience.titleClosePopup')"
                customClassTitle="poppinsSemiBold"
                noScroll
            >
                <template #content>
                    <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                        {{ $t('experience.descriptionClosePopup') }}
                    </p>
                </template>
                <template #actions>
                    <BaseButton
                        type="button"
                        variant="outline"
                        @click="_onConfirmDialog"
                        customHeight="43"
                        customClass="!ppv2-min-h-[40px]"
                    >
                        {{ $t('experience.btnLeave') }}
                    </BaseButton>
                    <BaseButton
                        type="button"
                        variant="primary"
                        @click="_onCancelDialog"
                        customHeight="43"
                        customClass="!ppv2-min-h-[40px]"
                        :isLargeBtn="true"
                    >
                        {{ $t('experience.btnStay') }}
                    </BaseButton>
                </template>
            </CustomDialog>
        </v-container>
    </v-app>
</template>

<script>
import Step1 from './components/Step1.vue'
import Step2 from './components/Step2.vue'
import Step3 from './components/Step3.vue'
import Step4 from './components/Step4.vue'
import Step5 from './components/Step5.vue'
import StepTicketOptions from './components/StepTicketOptions.vue'
import StepTicketAvailability from './components/StepTicketAvailability.vue'
import Step6 from './components/Step6.vue'
import Step7 from './components/Step7.vue'
import Step8 from './components/Step8.vue'
import { theme } from '../../../utils/theme'
import normalizeSize from '../../../utils/normalizeSize'
import HeaderStep from './components/HeaderStep.vue'
import CustomButtonSave from '../../Shared/CustomButtonSave.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'

import { Check, ChevronLeft, ChevronRight } from 'lucide-vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { EventBus } from '../../../eventBus'
import BaseButton from '../../Shared/BaseButton.vue'

export default {
    components: { CustomButtonSave, HeaderStep, CustomDialog, Check, ChevronLeft, ChevronRight, BaseButton },
    props: {
        id: {
            type: [Number, String],
            default: null,
        },
        type: {
            type: [String],
            default: null,
        },
        prestation: {
            type: Object,
        },
        selected_experience: {
            type: Object,
        },
        spaces_id: {
            type: Array,
            default: () => [],
        },
        country_vat_rates: {
            type: Array,
        },
        products: {
            type: Array,
        },
        cats: {
            type: Array,
            default: () => [],
        },
        category: {
            type: Object,
        },
        sub_category: {
            type: Object,
        },
        complited: {
            type: [Boolean],
            default: false,
        },
        urlFrontReservation: {
            type: String,
            default: '',
        },
        tenant: {
            type: Object,
            default: () => null,
        },
        planFormule: {
            type: Object,
            default: () => null,
        },
        planForfait: {
            type: Object,
            default: () => null,
        },
        planAbonnement: {
            type: Object,
            default: () => null,
        },
        planPublishAdditionalSales: {
            type: Object,
            default: () => null,
        },
        permissionCreateAdditionalSales: {
            type: Boolean,
            default: false,
        },
        permissionModifyAdditionalSales: {
            type: Boolean,
            default: false,
        },
        permissionDeleteAdditionalSales: {
            type: Boolean,
            default: false,
        },
        token: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            step: 1,
            highestStepReached: 2,
            isPageLoading: !!(this.id || this.prestation?.id),
            isStepTransitioning: false,
            isInitialLoad: true,
            showConfirmDialog: false,
            _pendingConfirm: null,
            _pendingCancel: null,
            // steps: [], // Removed, now computed
            localPrestation: this.prestation || {},
            localSelectType: this.selected_experience || {},
            localSpacesId: this.spaces_id || [],
            localProducts: this.products || [],
            allStepsConstant: [
                /*{
                    id: 1,
                    subtitle: this.$t('experience.step1.subtitle'),
                    content: this.$t('experience.step1.content'),
                    component: Step1,
                },*/
                {
                    id: 2,
                    subtitle: this.$t('experience.step2.subtitle'),
                    content: this.complited
                        ? this.$t('experience.step2.content').replace('Créer', 'Modifier')
                        : this.$t('experience.step2.content'),
                    component: Step2,
                },
                {
                    id: 3,
                    subtitle: this.$t('experience.step3.subtitle'),
                    content: this.$t('experience.step3.content'),
                    sub_content: this.$t('experience.step3.sub_content'),
                    component: Step3,
                },
                {
                    id: 4,
                    subtitle: this.$t('experience.step4.subtitle'),
                    component: Step4,
                },
                {
                    id: 5,
                    subtitle: this.$t('experience.step5.subtitle'),
                    component: Step5,
                },
                {
                    id: 55,
                    subtitle: this.$t('experience.stepTicketOptions.subtitle'),
                    component: StepTicketOptions,
                },
                {
                    id: 6,
                    subtitle: this.$t('experience.step6.subtitle'),
                    component: Step6,
                },
                {
                    id: 7,
                    subtitle: this.$t('experience.step7.subtitle'),
                    content: '',
                    component: Step7,
                },
                {
                    id: 8,
                    subtitle: 'Finish',
                    content: '',
                    component: Step8,
                },
            ],
            steps: [], // Initialize as empty array
        }
    },
    created() {
        // Always start at Step 2 (Step 1 is now in create-simple.vue)
        this.step = this.id ? 2 : 2
        this.updateSteps() // Initial update
    },
    methods: {
        normalizeSize,
        normalizeFontSize,
        _confirmLeave(onConfirm, onCancel) {
            this._pendingConfirm = onConfirm
            this._pendingCancel = onCancel || null
            this.showConfirmDialog = true
        },
        _onConfirmDialog() {
            this.showConfirmDialog = false
            if (typeof this._pendingConfirm === 'function') {
                this._pendingConfirm()
            }
            this._pendingConfirm = null
            this._pendingCancel = null
        },
        _onCancelDialog() {
            this.showConfirmDialog = false
            if (typeof this._pendingCancel === 'function') {
                this._pendingCancel()
            }
            this._pendingConfirm = null
            this._pendingCancel = null
        },
        updateSteps() {
            console.log('🔄 Updating steps...')
            if (!this.allStepsConstant) return

            let filtered = this.allStepsConstant

            // Always filter Step 1 (it's now in create-simple.vue)
            filtered = filtered.filter((s) => s.id !== 1)

            // Billetterie : l'étape 4 n'est plus retirée, son contenu est REMPLACÉ par l'étape
            // « Disponibilités » propre au billet (modes journée entière / créneaux fixes /
            // créneaux personnalisés).
            //
            // On garde délibérément l'id 4 : la reprise de création fait correspondre
            // `prestation.steps` (le n° d'étape stocké en base) à `s.id` ci-dessous. Avec un id
            // différent, `findIndex` échouerait et le wizard repartirait à l'étape 2.
            //
            // `free_access_all_day` est le mode de réservation du seul circuit simpleTicket
            // (cf. store/modules/experience.js) : aucun autre circuit n'est affecté.
            if (this.isFreeAccessMode) {
                filtered = filtered.map((s) =>
                    s.id === 4
                        ? {
                              ...s,
                              subtitle: this.$t('experience.stepAvailability.subtitle'),
                              component: StepTicketAvailability,
                          }
                        : s
                )
            }

            // Show Step 55 (Ticket Options) only for simpleTicket circuit
            if (!this.isSimpleTicketMode) {
                filtered = filtered.filter((s) => s.id !== 55)
            }

            // Dynamically assign step numbers
            this.steps = filtered.map((s, index) => ({
                ...s,
                stepNumber: index + 1,
            }))
        },
        handleNextStep(currentStepId, payload) {
            const currentIndex = this.steps.findIndex((s) => s.id === currentStepId)
            if (currentIndex >= 0 && currentIndex < this.steps.length - 1) {
                const nextStepId = this.steps[currentIndex + 1].id
                this.goToStep(nextStepId, payload)
            }
        },
        handleUpdateGlobalProducts(products) {
            // Fusion par id, jamais un remplacement : le serveur ne renvoie que les produits qu'il
            // vient de créer. Écraser la liste ferait disparaître tout le catalogue de
            // l'établissement des cartes de l'étape 5 jusqu'au rechargement de la page.
            const byId = new Map(this.localProducts.map((p) => [p.id, p]))
            products.forEach((p) => byId.set(p.id, { ...byId.get(p.id), ...p }))
            this.localProducts = Array.from(byId.values())
        },
        async handleSaveAndExit() {
            const refName = 'step' + this.step // génère 'step2', 'step3', ...
            const stepComponent = this.$refs[refName]

            if (!stepComponent) {
                console.warn(`⚠️ Ref ${refName} introuvable`)
                return
            }

            // Si c'est un tableau (cas avec v-for), prends le premier élément
            const instance = Array.isArray(stepComponent) ? stepComponent[0] : stepComponent

            // Vérifie que la méthode existe
            if (typeof instance.saveExperience === 'function') {
                await instance.saveExperience(true)
            } else {
                console.warn(`⚠️ La méthode saveExperience n'existe pas sur ${refName}`)
            }
        },

        async fetchPrestatonFromSteps(id, type) {
            this.isPageLoading = true
            try {
                const response = await axios.get('/api/v3/experience/' + id + '/' + type, {
                    params: { _timestamp: Date.now() },
                })
                const prestation = response.data.prestation

                this.localPrestation = prestation
                this.localSpacesId = response.data.spaces_id
                this.localSelectType = { type: type }

                if (!this.complited) {
                    // Mode création : reprendre à l'étape en cours (localStorage) ou la dernière sauvegardée (DB)
                    const prestationId = prestation.id || this.id
                    const localStep = Number(localStorage.getItem(`experience_step_${prestationId}`))
                    const localHighest = Number(localStorage.getItem(`experience_highest_${prestationId}`))

                    if (localStep && this.steps.findIndex((s) => s.id === localStep) >= 0 && localStep !== 8) {
                        this.step = localStep
                        this.highestStepReached = localHighest || localStep
                    } else if (prestation.steps) {
                        const savedStep = Number(prestation.steps)
                        const savedIndex = this.steps.findIndex((s) => s.id === savedStep)
                        if (savedIndex >= 0 && savedStep !== 8) {
                            this.step = savedStep
                            this.highestStepReached = savedStep
                        }
                    }
                    this.$nextTick(() => {
                        this.isInitialLoad = false
                    })
                    return
                }

                // Mode édition : aller directement à Tarification (étape la plus utilisée)
                this.step = 5
                this.$nextTick(() => {
                    this.isInitialLoad = false
                })
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.isPageLoading = false
            }
        },
        goToStep(nextStep, payload = null) {
            this.showButtons = false
            this.isStepTransitioning = true
            if (payload) {
                this.localSelectType = payload
            }
            const nextStepIndex = this.steps.findIndex((s) => s.id === nextStep)
            const highestIndex = this.steps.findIndex((s) => s.id === this.highestStepReached)
            if (nextStepIndex > highestIndex) {
                this.highestStepReached = nextStep
            }
            // Nettoyer localStorage quand la création est terminée
            if (nextStep === 8) {
                const prestationId = this.localPrestation?.id || this.id
                if (prestationId) {
                    localStorage.removeItem(`experience_step_${prestationId}`)
                    localStorage.removeItem(`experience_highest_${prestationId}`)
                }
            }
            setTimeout(() => {
                this.step = nextStep
                this.showButtons = true
                this.$nextTick(() => {
                    this.isStepTransitioning = false
                })
            }, 300)
        },
        goToPevious(previousStep) {
            this.isStepTransitioning = true
            setTimeout(() => {
                this.step = previousStep
                this.$nextTick(() => {
                    this.isStepTransitioning = false
                })
            }, 300)
        },
        goToPreviousStep(currentStepId) {
            const currentIndex = this.steps.findIndex((s) => s.id === currentStepId)
            if (currentIndex > 0) {
                const prevStepId = this.steps[currentIndex - 1].id
                if (this.hasAnyUnsavedChanges()) {
                    this._confirmLeave(() => {
                        this.goToPevious(prevStepId)
                    })
                } else {
                    this.goToPevious(prevStepId)
                }
            }
        },
        finish() {
            alert('ok')
        },
        goToExperiencesList() {
            window.location.href = '/back-office/v3/experience'
        },
        handleStepClick(targetStep) {
            if (targetStep === this.step) return

            const refName = 'step' + this.step
            const stepComponent = this.$refs[refName]
            const instance = Array.isArray(stepComponent) ? stepComponent[0] : stepComponent

            if (instance && typeof instance.hasChanges === 'function' && instance.hasChanges()) {
                this._confirmLeave(() => {
                    this.executeStepChange(targetStep)
                })
                return
            }

            this.executeStepChange(targetStep)
        },
        executeStepChange(targetStep) {
            const targetIndex = this.steps.findIndex((s) => s.id === targetStep)
            const highestIndex = this.steps.findIndex((s) => s.id === this.highestStepReached)
            if (this.complited || targetIndex <= highestIndex) {
                this.isStepTransitioning = true
                setTimeout(() => {
                    this.step = targetStep
                    this.$nextTick(() => {
                        this.isStepTransitioning = false
                    })
                }, 200)
            } else {
                console.warn('Cannot go to a future step without completing previous steps first.')
            }
        },
        handleRequestNavigation(navEvent) {
            if (this.hasAnyUnsavedChanges()) {
                navEvent.defaultPrevented = true
            }
        },
        // Returns true if the currently active step has unsaved changes
        hasAnyUnsavedChanges() {
            if (this._forceExit) return false
            const refName = 'step' + this.step
            const comp = this.$refs[refName]
            const instance = Array.isArray(comp) ? comp[0] : comp
            return !!(instance && typeof instance.hasChanges === 'function' && instance.hasChanges())
        },

        isStepCompleted(sId) {
            const currentIndex = this.steps.findIndex((s) => s.id === this.step)
            const targetIndex = this.steps.findIndex((s) => s.id === sId)
            return currentIndex > targetIndex
        },
        isStepDisabled(sId) {
            const highestIndex = this.steps.findIndex((s) => s.id === this.highestStepReached)
            const targetIndex = this.steps.findIndex((s) => s.id === sId)
            return targetIndex > highestIndex
        },
    },
    computed: {
        sizes() {
            return {
                title: this.normalizeSize(32, true),
                subtitle: this.normalizeSize(14, true),
                subtitleMenu: this.normalizeSize(13, true),
                formStep: this.normalizeSize(15, true),
            }
        },
        hasId() {
            return this.id !== null && this.id !== undefined
        },
        isFreeAccessMode() {
            const mode = this.$store.getters['experience/selectedReservationMode']
            if (mode) return mode === 'free_access_all_day'

            // Fallback to localPrestation
            if (this.localPrestation && this.localPrestation.user_choices) {
                const choice = this.localPrestation.user_choices.find((c) => c.key === 'reservation_mode')
                return choice && choice.value === 'free_access_all_day'
            }
            return false
        },
        isSimpleTicketMode() {
            const circuits = this.$store.getters['experience/getMatchingCircuits']
            return circuits && circuits.length === 1 && circuits[0] === 'simpleTicket'
        },
        shouldHidePreview() {
            return false
        },
    },
    mounted() {
        if (this.id) {
            // If an explicit id is provided, use it
            this.fetchPrestatonFromSteps(this.id, this.type)
        } else if (this.localPrestation?.id) {
            // If prestation object exists and has an id, use it
            this.fetchPrestatonFromSteps(this.localPrestation.id, this.localSelect)
        }

        // Warn when the user tries to close the tab or navigate via the browser address bar
        this._beforeUnloadHandler = (e) => {
            if (this.hasAnyUnsavedChanges()) {
                e.preventDefault()
                e.returnValue = ''
            }
        }
        window.addEventListener('beforeunload', this._beforeUnloadHandler)

        // Warn on Turbo visits (e.g. Sidebar clicks, Logout)
        this._turboBeforeVisitHandler = (e) => {
            if (this.hasAnyUnsavedChanges()) {
                e.preventDefault()
                const url = e.detail.url
                this._confirmLeave(() => {
                    this._forceExit = true
                    if (this._beforeUnloadHandler) {
                        window.removeEventListener('beforeunload', this._beforeUnloadHandler)
                    }
                    if (window.Turbo) {
                        window.Turbo.visit(url)
                    } else {
                        window.location.href = url
                    }
                })
            }
        }
        document.addEventListener('turbo:before-visit', this._turboBeforeVisitHandler)

        // Listen for manual hard navigations (e.g. from NavBar logout)
        EventBus.$on('request-navigation', this.handleRequestNavigation)

        // Listen for forced navigation (user confirmed in NavBar dialog)
        this._handleForceNavigation = () => {
            if (this._beforeUnloadHandler) {
                window.removeEventListener('beforeunload', this._beforeUnloadHandler)
            }
            this._forceExit = true
        }
        EventBus.$on('force-navigation', this._handleForceNavigation)
    },
    beforeRouteLeave(to, from, next) {
        if (this.hasAnyUnsavedChanges()) {
            this._confirmLeave(
                () => {
                    this._forceExit = true
                    if (this._beforeUnloadHandler) {
                        window.removeEventListener('beforeunload', this._beforeUnloadHandler)
                    }
                    next()
                },
                () => {
                    next(false)
                }
            )
        } else {
            next()
        }
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this._beforeUnloadHandler)
        document.removeEventListener('turbo:before-visit', this._turboBeforeVisitHandler)
        EventBus.$off('request-navigation', this.handleRequestNavigation)
        EventBus.$off('force-navigation', this._handleForceNavigation)
    },
    watch: {
        step(newStep) {
            if (this.isInitialLoad) return
            const prestationId = this.localPrestation?.id || this.id
            if (prestationId && !this.complited) {
                localStorage.setItem(`experience_step_${prestationId}`, newStep)
                localStorage.setItem(`experience_highest_${prestationId}`, this.highestStepReached)
            }
        },
        isFreeAccessMode: {
            handler() {
                this.updateSteps()
            },
            immediate: true,
        },
        isSimpleTicketMode: {
            handler() {
                this.updateSteps()
            },
        },
        id: {
            handler() {
                this.updateSteps()
            },
            immediate: true,
        },
    },
}
</script>
<style scoped>
.v-sheet.v-stepper:not(.v-sheet--outlined) {
    box-shadow: none;
    height: 100%;
}

.step_title_content {
    font-family: 'Poppins Light', sans-serif;
    text-align: center;
    margin-bottom: 0;
}

/* Stepper container */
.stepper-container {
    display: flex;
    flex-direction: column;
    position: relative;
    padding-left: 40px;
    border-right: 0.5px solid #5f5fa3;
}

/* Step item */
.step-item {
    position: relative;
    display: flex;
    align-items: flex-start;
    cursor: pointer;
    min-height: 80px;
}

.stepper-collapsed {
    padding-left: 12px !important;
    padding-right: 4px !important;
}
.stepper-collapsed .step-item {
    min-height: 50px;
    justify-content: center;
}
.stepper-collapsed .circle {
    margin: 0 auto;
}
.stepper-collapsed .line {
    display: none;
}
.nav-toggle-btn {
    background: transparent;
    border: 1px solid rgba(84, 85, 149, 0.2);
}
.nav-toggle-btn:hover {
    background: rgba(84, 85, 149, 0.08);
}
.stepper-container-edit {
    background-color: rgba(0, 0, 0, 0.05);
    border-right: none !important;
    padding-left: 0 !important;
}

.dark .stepper-container-edit {
    background-color: #d9d9d90d;
}

/* Circle base */
.circle {
    position: relative;
    width: 18px;
    min-width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid #021832;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
}

.dark .circle {
    border: 2px solid #fff;
}

/* Ligne verticale */
.line {
    position: absolute;
    left: 10px;
    top: 30px;
    width: 1px;
    height: 40px;
    background: #021832;
    opacity: 0.3;
    z-index: 1;
}

.dark .line {
    background: #fff;
}

/* ✅ Étape terminée */
.step-item.completed .circle {
    background: #06b217;
    border-color: #06b217;
}

.step-item.completed .line {
    background: #06b217;
}

/* 🟣 Étape active */
.step-item.active .circle {
    border-color: #7d2dbd;
    box-shadow: 0 0 8px #7d2dbd;
}

.step-title {
    font-family: 'Roboto Medium';
    text-transform: uppercase;
}

.step-subtitle {
    font-family: 'Roboto Medium';
}

/* Étapes à venir (non actives) */
.step-item:not(.active):not(.completed) .step-title,
.step-item:not(.active):not(.completed) .step-subtitle,
.step-item:not(.active):not(.completed) .circle {
    opacity: 0.3;
}

.step-item.activeMenu {
    border-left: 3px solid;
    border-left-color: #7d2dbd;
    background-color: #1a1f371a;

    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
    max-width: 80%;
}

.dark .step-item.activeMenu {
    background-color: rgba(255, 255, 255, 0.09);
}

.step-item.activeMenu *,
.step-item.completedMenu * {
    opacity: 1 !important;
}

.form-header {
    margin-bottom: 40px;
}

.form-step {
    text-transform: uppercase;
    margin-bottom: 6px;
}

/* use from create */
.step-item.disabled {
    pointer-events: none;
    cursor: not-allowed;
    opacity: 0.4;
}

.step-item.completed {
    cursor: pointer;
}

/* ===== Loading Skeleton Styles ===== */
.skeleton-line {
    border-radius: 6px;
    background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
}

.dark .skeleton-line {
    background: linear-gradient(90deg, #2a2a3e 25%, #3a3a50 50%, #2a2a3e 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
}

.skeleton-block {
    border-radius: 8px;
    background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
}

.dark .skeleton-block {
    background: linear-gradient(90deg, #2a2a3e 25%, #3a3a50 50%, #2a2a3e 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
}

/* Upload zone skeleton — matches real .upload-box */
.skeleton-upload-box {
    height: 147px;
    width: 100%;
    border: 1.5px dashed #9c9af840;
    border-radius: 12px;
    margin-bottom: 32px;
    background: linear-gradient(
        90deg,
        rgba(244, 250, 254, 0.02) 25%,
        rgba(244, 250, 254, 0.06) 50%,
        rgba(244, 250, 254, 0.02) 75%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
}

.dark .skeleton-upload-box {
    border-color: #9c9af820;
    background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.02) 25%,
        rgba(255, 255, 255, 0.04) 50%,
        rgba(255, 255, 255, 0.02) 75%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
}

/* Small circle for eye icon placeholder */
.skeleton-circle-sm {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
}

.dark .skeleton-circle-sm {
    background: linear-gradient(90deg, #2a2a3e 25%, #3a3a50 50%, #2a2a3e 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
}

/* Progress bar skeleton — matches .preview-progress */
.skeleton-progress {
    height: 7px;
    background: #9b9b9b33;
    border-radius: 3px;
    margin-bottom: 16px;
    overflow: hidden;
}

.skeleton-progress-bar {
    width: 80%;
    height: 100%;
    background: linear-gradient(90deg, #3a329d44 27%, #8241d744 100%);
    border-radius: 3px;
    animation: shimmer 1.5s ease-in-out infinite;
    background-size: 200% 100%;
}

@keyframes shimmer {
    0% {
        background-position: 200% 0;
    }

    100% {
        background-position: -200% 0;
    }
}

/* Fade transition for step changes */
.fade-step-enter-active,
.fade-step-leave-active {
    transition:
        opacity 0.25s ease,
        transform 0.25s ease;
}

.fade-step-enter {
    opacity: 0;
    transform: translateY(10px);
}

.fade-step-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}
::v-deep .v-stepper__wrapper {
    height: 100%;
}
</style>
